import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { courseService, Course } from '../services/courseService';
import './ScoreInsertModal.css';

interface ScoreInsertModalProps {
  isOpen: boolean;
  onClose: () => void;
  classId: string;
  className: string;
}

const ScoreInsertModal: React.FC<ScoreInsertModalProps> = ({ isOpen, onClose, classId, className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [error, setError] = useState('');

  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [examDate, setExamDate] = useState('');

  useEffect(() => {
    if (isOpen && classId) {
      loadCourses();
    }
  }, [isOpen, classId]);

  const loadCourses = async () => {
    try {
      const data = await courseService.getAllCourses();
      setCourses(data);
    } catch (e: any) {
      console.error('Failed to load courses:', e);
    }
  };

  const handleNext = () => {
    // Validation
    if (!selectedCourseId) {
      setError(t('scores.selectCourse'));
      return;
    }
    if (!examDate) {
      setError(t('scores.selectExamDate'));
      return;
    }

    // Navigate to score insert page with params
    navigate(`/scores/insert?classId=${classId}&courseId=${selectedCourseId}&examDate=${examDate}`);
    handleClose();
  };

  const handleClose = () => {
    setSelectedCourseId('');
    setExamDate('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content scores-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('scores.insertScores')} - {className}</h3>
          <button onClick={handleClose} className="btn-close">×</button>
        </div>

        <div className="modal-body">
          {error && <div className="error-message">{error}</div>}

          {/* Select Course */}
          <div className="form-field">
            <label>{t('scores.course')} *</label>
            <select
              value={selectedCourseId}
              onChange={(e) => setSelectedCourseId(e.target.value)}
            >
              <option value="">{t('scores.selectCourse')}</option>
              {courses
                .filter(c => c.classIds?.includes(classId))
                .map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Select Exam Date */}
          <div className="form-field">
            <label>{t('scores.examDate')} *</label>
            <input
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
            />
          </div>
        </div>

        {/* Modal Actions */}
        <div className="modal-footer">
          <button onClick={handleClose} className="btn-cancel">
            {t('common.cancel')}
          </button>
          <button onClick={handleNext} className="btn-next">
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoreInsertModal;
